/**
 * ログインの間の「待っている印」（2026-10-21 core/account-login-design）。
 *
 * founder（10/21・Quick Draw のテスト版で本物で押して）: 「ログインできたけどコンティニュー押したときとか、ゲームにもどってきたときとか、
 *   しばらく何も動かないから不安になる。動いている間はロード中とか接続中のアニメーションが絶対に必要」
 *
 * 出す間:
 *   - signInWithProvider / linkProvider を呼んでから終わるまで（ポップアップでログインしている間・閉じてからコードを換える間）
 *   - リダイレクトで戻ったページで completeRedirectSignIn がコードを換える間（init が自動で呼ぶ）
 *   ポップアップが止められて同じタブで移る形（別メンバー core/signin-popup-fallback）では、ページを離れるまで出たまま。
 * 作り手が何もしなくても出る（AI に頼むだけでログインが付く = 作り手は印を作らないことが多い）。
 * 上書き: kumo.auth.setSignInIndicator(false)（出さない）/ setSignInIndicator((s) => …)（自分の画面で出す。組み込みの印は出ない）。
 *
 * 形: 画面の下の真ん中に小さな丸い札（回る輪 + 「Signing in…」/「ログイン中…」）。押せない（pointer-events: none）= ゲームの操作を邪魔しない。
 *   読み上げ: role=status。動きを減らす設定の人には回さない。
 * WHY <style> を足さず要素の style と Web Animations で描くか: ゲームのページが CSP で inline の <style> を止めていても出る
 *   （要素の style 属性を JS で付けるのは CSP の style-src に止められない・element.animate も同じ）。
 * WHY 二度押しを止めるか: 待っている間に同じボタンをもう一度押すと、2 つ目のポップアップ・2 つ目の往復が始まり、1 つ目の結果が迷子になる。
 *   同じ入り方を待っている間の 2 回目は、1 回目と同じ結果（同じ Promise）を返す。別の入り方なら KumoError('signin_in_progress')。
 */

import { KumoError } from './core.js';
import type { BrowserLike, OAuthProvider } from './authProviders.js';

export interface SignInWaitingState {
  /** true = ログインの往復の途中（印を出す）/ false = 終わった（消す） */
  busy: boolean;
  /** どの入り方か。リダイレクトから戻ったページでは分からないので null */
  provider: OAuthProvider | null;
  /** 'signing-in' = ボタンを押してから / 'returning' = リダイレクトで戻ったページでコードを換えている */
  phase: 'signing-in' | 'returning';
}

/** false = 印を出さない / 関数 = 作り手の印（組み込みは出さない） */
export type SignInIndicator = false | ((state: SignInWaitingState) => void);

/** 組み込みの印に要るブラウザの部分（テストで差し替える） */
interface DomLike {
  document?: {
    createElement(tag: string): ElementLike;
    body?: { appendChild(el: ElementLike): unknown } | null;
    documentElement?: { lang?: string };
  };
  navigator?: { language?: string };
  matchMedia?(q: string): { matches: boolean };
}
interface ElementLike {
  style: Record<string, string> & { cssText?: string };
  textContent: string | null;
  setAttribute(k: string, v: string): void;
  appendChild(el: ElementLike): unknown;
  remove?(): void;
  animate?(frames: Array<Record<string, string>>, opts: Record<string, unknown>): unknown;
}

const TEXT = {
  en: { 'signing-in': 'Signing in…', returning: 'Finishing sign-in…' },
  ja: { 'signing-in': 'ログイン中…', returning: 'ログインを仕上げています…' }
} as const;

/** ゲームの言語（<html lang>）→ ブラウザの言語の順。日本語だけ日本語、ほかは英語 */
function langOf(w: DomLike): 'en' | 'ja' {
  const l = (w.document?.documentElement?.lang || w.navigator?.language || 'en').toLowerCase();
  return l.startsWith('ja') ? 'ja' : 'en';
}

/** 組み込みの印。出す・消すだけ（同時に 1 つ） */
function builtIn(win: () => DomLike | null) {
  let pill: ElementLike | null = null;
  let label: ElementLike | null = null;
  return (s: SignInWaitingState) => {
    if (!s.busy) {
      pill?.remove?.();
      pill = label = null;
      return;
    }
    const w = win();
    const d = w?.document;
    if (!w || !d?.body) return;
    const text = TEXT[langOf(w)][s.phase];
    if (pill && label) {
      label.textContent = text;
      return;
    }
    pill = d.createElement('div');
    pill.setAttribute('role', 'status');
    pill.setAttribute('aria-live', 'polite');
    pill.setAttribute('data-kumo', 'signin-waiting');
    // 墨の地・紙の字（KUMODeck の中立の画面と同じ色）。ゲームの絵の上でも読めるよう不透明に近く。いちばん上に重ねる
    Object.assign(pill.style, {
      position: 'fixed', left: '50%', bottom: '24px', transform: 'translateX(-50%)', zIndex: '2147483647',
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderRadius: '999px',
      background: 'rgba(23, 20, 15, 0.92)', color: '#f3eee4', font: '600 14px/1.2 system-ui, -apple-system, "Segoe UI", sans-serif',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)', pointerEvents: 'none', whiteSpace: 'nowrap'
    });
    const ring = d.createElement('span');
    ring.setAttribute('aria-hidden', 'true');
    Object.assign(ring.style, { width: '14px', height: '14px', borderRadius: '999px', border: '2px solid currentColor', borderRightColor: 'transparent', display: 'inline-block', flex: 'none' });
    // 動きを減らす設定の人には回さず、ゆっくり明滅する（サーバーのページの .spinner と同じ）
    const reduce = w.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    ring.animate?.(
      reduce ? [{ opacity: '1' }, { opacity: '0.35' }, { opacity: '1' }] : [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: reduce ? 1600 : 800, iterations: Infinity }
    );
    label = d.createElement('span');
    label.textContent = text;
    pill.appendChild(ring);
    pill.appendChild(label);
    d.body.appendChild(pill);
  };
}

/** ゲームの fragment に往復の結果（auth_code / auth_error・旧名 kumo_…）があるか。authProviders.ts parseFragment と同じ名前 */
const RETURN_RE = /(?:^#|&)(?:auth|kumo)_(?:code|error)=/;

export function createSignInWaiting(browser: () => (BrowserLike & DomLike) | null, pendingKey: string) {
  let indicator: SignInIndicator | undefined;
  const fallback = builtIn(browser);
  const emit = (s: SignInWaitingState) => {
    const fn = indicator === undefined ? fallback : indicator;
    if (!fn) return;
    try {
      fn(s);
    } catch (e) {
      // 印の失敗でログインを止めない
      console.warn('[game-backend] sign-in indicator failed', e);
    }
  };

  let inFlight: { provider: OAuthProvider; link: boolean; promise: Promise<unknown> } | null = null;

  /** 押してから終わるまで印を出す。start は同期で呼ぶ（ポップアップはクリックの中で開く必要がある = await を挟まない） */
  function around<T>(provider: OAuthProvider, link: boolean, start: () => Promise<T>): Promise<T> {
    if (inFlight) {
      if (inFlight.provider === provider && inFlight.link === link) return inFlight.promise as Promise<T>;
      return Promise.reject(new KumoError(0, 'signin_in_progress', 'Another sign-in is still going. Finish or close it first.'));
    }
    emit({ busy: true, provider, phase: 'signing-in' });
    let promise: Promise<T>;
    try {
      promise = start();
    } catch (e) {
      emit({ busy: false, provider, phase: 'signing-in' });
      throw e;
    }
    const done = () => {
      if (inFlight?.promise === promise) inFlight = null;
      emit({ busy: false, provider, phase: 'signing-in' });
    };
    inFlight = { provider, link, promise };
    promise.then(done, done);
    return promise;
  }

  /** リダイレクトで戻ったページ: このタブで始めた往復の結果が URL にあるときだけ、換える間の印を出す */
  function aroundReturn<T>(complete: () => Promise<T>): Promise<T> {
    const b = browser();
    let returning = false;
    try {
      returning = !!b && RETURN_RE.test(b.location.hash) && !!b.sessionStorage?.getItem(pendingKey);
    } catch {
      returning = false; // sessionStorage が使えない = SDK の保存領域で往復している。印を出さないだけ
    }
    const p = complete();
    if (!returning) return p;
    emit({ busy: true, provider: null, phase: 'returning' });
    const off = () => emit({ busy: false, provider: null, phase: 'returning' });
    p.then(off, off);
    return p;
  }

  return {
    around,
    aroundReturn,
    /** 印を差し替える: false = 出さない / 関数 = 自分で出す / undefined = 組み込みに戻す */
    setIndicator(next: SignInIndicator | undefined) {
      indicator = next;
    }
  };
}
